/**
 * core · catálogos (spec 03 §2 · tela 10.1).
 *
 * **Um lugar só.** A tela 10.1 administra 28 catálogos; a API os expõe por uma rota
 * genérica `/catalogos/:tipo`. Escrever isso duas vezes — 28 formulários React de um
 * lado, 28 blocos de SQL do outro — seria 56 lugares para divergir, e a divergência
 * apareceria como "o campo existe na tela e não grava".
 *
 * Então o mapa catálogo → tabela → campos é DADO, declarado aqui e consumido pelos
 * dois lados: o servidor monta o SQL a partir dele (e por isso nenhum identificador
 * vem do cliente — o `:tipo` só seleciona uma linha desta lista) e a tela monta a
 * navegação, a lista e o modal.
 *
 * O que NÃO está aqui: regra de negócio. "Excluir só com uso 0" (DS-27), a trava de
 * semente (DS-59) e a vigência sem sobreposição são do serviço e do banco.
 */
import { z } from 'zod'
import { COMPORTAMENTOS_DE_VENDA } from '@dv/domain'
import { AVISO_DA_EMISSAO_AUTOMATICA } from './aviso-da-emissao.js'
import { FRACAO_ENTRE_ZERO_E_UM } from './comum.js'

export type TipoDeCampo =
  | 'texto'
  | 'texto-longo'
  | 'inteiro'
  | 'percentual'
  | 'booleano'
  | 'data'
  | 'escolha'
  | 'referencia'
  | 'json'

export interface CampoDeCatalogo {
  /** Nome da COLUNA no banco — é o que o servidor usa para montar o SQL. */
  readonly coluna: string
  readonly rotulo: string
  readonly tipo: TipoDeCampo
  readonly obrigatorio: boolean
  /** Uma linha em voz de produto, exibida sob o campo (spec 05 §16). */
  readonly microcopy?: string
  /** `tipo: 'escolha'` — os valores exatos do CHECK da tabela. */
  readonly opcoes?: readonly string[]
  /**
   * DS-550: o rótulo que a tela mostra para cada VALOR de `opcoes` (no chip da lista e no
   * seletor do modal). `opcoes` continua sendo a lista de valores do CHECK, que o servidor
   * confere e que se grava; o rótulo é só apresentação. Sem ele, a tela mostra o próprio
   * valor (ver `rotuloDaOpcao`). O mecanismo nasceu com a Forma de venda (D-146, DS-553).
   */
  readonly rotulosDasOpcoes?: Readonly<Record<string, string>>
  /** `tipo: 'referencia'` — o slug do catálogo referenciado. */
  readonly referencia?: string
  /** Referência cujo nome a lista mostra como chip (D-146): a lista carrega o catálogo dela. */
  readonly chipNaLista?: boolean
  /** Não editável depois de criado (chave natural: ISO de moeda, ISO de país). */
  readonly imutavel?: boolean
  /**
   * DS-541: o rótulo da opção EM BRANCO de um campo opcional, de escolha ou de referência.
   * Diz o que o vazio SIGNIFICA ("Herda do produto"), porque em alguns catálogos o vazio é
   * desenho e não falta de preenchimento. Sem ele, o rótulo é "Nenhum".
   */
  readonly rotuloDoVazio?: string
}

export interface DescritorDeCatalogo {
  readonly slug: string
  readonly tabela: string
  readonly rotulo: string
  /**
   * Uma ou duas frases em voz de produto: o que o catálogo é e onde aparece (DS-557). A 10.1
   * a mostra sob o título e a contagem, e no modal de Adicionar e de Editar. Obrigatória no
   * tipo: catálogo novo não compila sem ela. Sem travessão: a catraca de escrita não lê este
   * pacote, e a guarda é o catalogos.test.ts.
   */
  readonly descricao: string
  /**
   * O efeito REAL de desativar um item, onde o genérico ("saem das opções, sem afetar
   * registros existentes") é falso (DS-557). Só texto: nenhuma regra muda.
   */
  readonly efeitoDeDesativar?: string
  /** Coluna da chave primária: `id` uuid em quase todas, `iso` em duas. */
  readonly chave: string
  /** Coluna que a lista mostra como título da linha. */
  readonly rotuloDaLinha: string
  /** Nome REAL da coluna de ativo — `ativo` em quase todas, `ativa` em praça e função. */
  readonly colunaAtivo?: string
  /**
   * Existe a alça de arrastar? Só onde a tabela tem `ordem` (spec 04-TELAS/10.1). A alça
   * reordena por POSIÇÃO (`__ordem`, DS-544), e o POST põe o item novo no fim.
   */
  readonly colunaOrdem?: string
  readonly campos: readonly CampoDeCatalogo[]
  /** Mostrados na lista, nunca no modal — o que descreve a linha sem ser editável. */
  readonly camposDeLeitura?: readonly CampoDeCatalogo[]
  /**
   * Vigência guardada como `daterange` (spec 02 §1.6 e §11.4).
   *
   * O banco guarda UMA coluna de intervalo — é o que permite ao EXCLUDE recusar
   * sobreposição —, mas o formulário tem dois campos. A tradução mora aqui, e não
   * espalhada em dois lugares que poderiam discordar sobre o intervalo ser aberto
   * ou fechado à direita.
   */
  readonly faixaDeVigencia?: { readonly coluna: string; readonly de: string; readonly ate: string }
  /** Catálogo de valor por chave: a 10.1 edita o VALOR, nunca cria/exclui a chave. */
  readonly somenteEdicao?: boolean
  /** Recorta pela entidade ativa do seletor (DS-40). Só as alíquotas. */
  readonly porEntidade?: boolean
  /** Tem coluna `semente`: item de seed não se exclui (DS-59). */
  readonly temSemente?: boolean
  /**
   * Tem coluna `chave_sistema` (DS-598): a linha que o sistema procura pela chave (a guia de tributo,
   * o RPA, a Transferência/PIX, o estorno do banco, o Outro da rejeição de desembolso) não se exclui.
   * O nome continua editável, porque a identidade técnica é a chave e não o rótulo.
   */
  readonly temChaveDeSistema?: boolean
  /**
   * Aviso fixo do catálogo sobre o que ele ainda NÃO faz (D-147, DS-556), desenhado acima da
   * lista e no modal. Não é a descrição do item 14: aquela diz para que o catálogo serve.
   */
  readonly aviso?: string
}

const nome = (rotulo = 'Nome'): CampoDeCatalogo => ({
  coluna: 'nome',
  rotulo,
  tipo: 'texto',
  obrigatorio: true,
})

/** O rótulo de uma opção de escolha: o de `rotulosDasOpcoes`, ou o próprio valor (DS-550). */
export function rotuloDaOpcao(campo: CampoDeCatalogo, valor: string): string {
  return campo.rotulosDasOpcoes?.[valor] ?? valor
}

/** Os 8 contextos do CHECK de `motivo` (DS-34), com o rótulo que a tela mostra. */
export const CONTEXTOS_DE_MOTIVO = [
  { valor: 'perda_negocio', rotulo: 'Perda de negócio' },
  { valor: 'encerramento_pedido', rotulo: 'Encerramento de pedido' },
  { valor: 'cancelamento_pedido', rotulo: 'Cancelamento de pedido' },
  { valor: 'cancelamento_nf', rotulo: 'Cancelamento de NF' },
  { valor: 'cancelamento_cap', rotulo: 'Cancelamento de CAP' },
  { valor: 'cancelamento_campanha', rotulo: 'Cancelamento de campanha' },
  { valor: 'ignorar_movimento', rotulo: 'Ignorar movimento' },
  { valor: 'rejeicao_desembolso', rotulo: 'Rejeição de desembolso' },
] as const

export const DESCRITORES: readonly DescritorDeCatalogo[] = [
  // ---------------------------------------------------------------- Comercial
  /*
    D-145/DS-551: recorrente, permite_parcelamento, usa_subprodutos, moeda_padrao e
    politica_receita não têm leitor de comportamento. Ficam no banco com o valor de cada linha,
    fora da leitura e da gravação da 10.1 (o servidor monta o SQL a partir destes campos).
  */
  {
    slug: 'tipos-produto',
    tabela: 'tipo_produto',
    rotulo: 'Tipos de produto',
    descricao:
      'Agrupa os produtos pelo jeito de gerar receita. Pedido só com produtos de tipo de revenda de mídia entra na DRE como Receita de repasse; sem nenhum, como Receita própria; com os dois, a receita é repartida pelo valor dos itens.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'revenda_com_custo',
        rotulo: 'Revenda de mídia de terceiros',
        tipo: 'booleano',
        obrigatorio: false,
        microcopy:
          'Mídia comprada de um fornecedor e revendida ao cliente. Na DRE, a receita destes produtos entra como Receita de repasse e a dos demais como Receita própria; o pedido com os dois é repartido pelo valor dos itens.',
      },
    ],
  },
  /*
    D-146/DS-553: a forma de venda é cadastro, com nome livre (só exibição) e um comportamento
    que o sistema lê. As quatro formas da carga inicial nascem na migration 0155, nunca no seed:
    até a v1.3.4 o seed rodava a cada deploy de produção e recriaria uma forma renomeada (desde a
    DS-597 ele só roda na instalação de um banco novo).
  */
  {
    slug: 'formas-venda',
    tabela: 'forma_venda',
    rotulo: 'Formas de venda',
    descricao:
      'As formas de vender um produto. O comportamento de cada uma decide o que o pedido pede: com veiculação (datas de início e término), sem veiculação, ou assinatura (cobrança por ciclo, em pedido próprio).',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'comportamento',
        rotulo: 'Comportamento',
        tipo: 'escolha',
        obrigatorio: true,
        opcoes: COMPORTAMENTOS_DE_VENDA,
        rotulosDasOpcoes: { com_veiculacao: 'Com veiculação', sem_veiculacao: 'Sem veiculação', assinatura: 'Assinatura' },
        microcopy:
          'Com veiculação: venda avulsa, e o pedido pede as datas de início e término da campanha. Sem veiculação: venda avulsa, sem datas. Assinatura: cobrança por ciclo, em pedido próprio, e o negócio mostra a mensalidade.',
      },
    ],
  },
  {
    slug: 'produtos',
    tabela: 'produto',
    rotulo: 'Produtos',
    descricao:
      'O que a DV vende. Aparece nos itens de negócios, pedidos e campanhas, e é eixo das Metas e do Faturamento por produto na Visão Geral.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'tipo_produto_id',
        rotulo: 'Tipo de produto',
        tipo: 'referencia',
        obrigatorio: true,
        referencia: 'tipos-produto',
        microcopy: 'Do tipo vem a conta da receita na DRE: Receita de repasse ou Receita própria.',
      },
      {
        coluna: 'forma_venda_id',
        rotulo: 'Forma de venda',
        tipo: 'referencia',
        referencia: 'formas-venda',
        obrigatorio: false,
        chipNaLista: true,
        rotuloDoVazio: 'Definida pelo sub-produto',
        microcopy: 'Em branco, é o sub-produto que diz a forma de venda. Deixe em branco só quando cada sub-produto tiver a sua.',
      },
      /*
        DS-552: o nome do glossário (Meta de custo de mídia) e onde o campo entra. Em branco não
        é "não compra mídia": sem fonte a margem fica pendente; 0% é que é a afirmação de que o
        produto não compra mídia. Desde a v1.9.0 a meta não projeta nada no Fluxo de Caixa
        (D-193 (9), DS-686 (j)), e o texto deixou de prometer isso.
      */
      {
        coluna: 'custo_midia_pct_default',
        rotulo: 'Meta de custo de mídia padrão',
        tipo: 'percentual',
        obrigatorio: false,
        microcopy:
          'Parte do valor de cada item de campanha que se espera gastar com mídia deste produto, quando a Composição não traz meta própria. Entra na margem até o primeiro apontamento e é o alvo do saldo de mídia. Produto que não compra mídia: 0%. Em branco, cada campanha pede a meta no item.',
      },
    ],
  },
  {
    slug: 'subprodutos',
    tabela: 'subproduto',
    rotulo: 'Sub-produtos',
    descricao:
      'Detalha um produto (ex.: YouTube em DV360). Aparece nos itens de negócios, pedidos e campanhas e no detalhamento por executivo das Metas.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      { coluna: 'produto_id', rotulo: 'Produto', tipo: 'referencia', obrigatorio: true, referencia: 'produtos' },
      {
        coluna: 'forma_venda_id',
        rotulo: 'Forma de venda',
        tipo: 'referencia',
        referencia: 'formas-venda',
        obrigatorio: false,
        chipNaLista: true,
        rotuloDoVazio: 'Herda do produto',
        microcopy: 'Vazio = herda a forma de venda do produto.',
      },
    ],
  },
  {
    slug: 'tipos-empresa',
    tabela: 'tipo_empresa',
    rotulo: 'Tipos de empresa',
    descricao:
      'Classifica as empresas do cadastro. Do tipo vêm o Setor (Público ou Privado) e o prazo de recebimento padrão da empresa; empresa sem tipo não pode ser Cliente.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'setor',
        rotulo: 'Setor',
        tipo: 'escolha',
        obrigatorio: true,
        opcoes: ['Público', 'Privado'],
        microcopy: 'Todas as empresas deste tipo seguem este Setor: mudá-lo muda também os meses passados da DRE e das Metas.',
      },
      {
        coluna: 'prazo_padrao_id',
        rotulo: 'Perfil de prazo padrão',
        tipo: 'referencia',
        obrigatorio: true,
        referencia: 'perfis-prazo',
        microcopy: 'Prazo de recebimento das empresas deste tipo que não têm prazo próprio.',
      },
    ],
  },
  {
    slug: 'tipos-atividade',
    tabela: 'tipo_atividade',
    rotulo: 'Tipos de atividade',
    descricao:
      'As opções do Registrar atividade nas fichas de Empresa, Contato e Negócio. O tipo dá o título do registro na linha do tempo e não muda nenhum cálculo.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'tipos-parceiro',
    tabela: 'tipo_parceiro',
    rotulo: 'Tipos de parceiro',
    descricao:
      'Classifica o parceiro comissionado: é escolhido ao habilitar uma empresa ou um contato como parceiro, e as Regras de comissão podem valer só para um tipo.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'estagios-funil',
    tabela: 'estagio_funil',
    rotulo: 'Estágios do funil',
    descricao:
      'Os estágios de Resultado Aberto são as colunas do quadro de Negócios, na ordem desta lista. Ganho e Perdido são os estágios finais, para onde vai o negócio marcado como ganho ou perdido. A probabilidade entra no pipeline ponderado.',
    efeitoDeDesativar:
      'Estágio inativo sai do quadro e das opções; os negócios que estão nele deixam de aparecer no quadro, mas seguem nos totais e na ficha. Sem nenhum estágio Ganho (ou Perdido) ativo, não se marca negócio como ganho (ou perdido).',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    colunaOrdem: 'ordem',
    campos: [
      nome(),
      {
        coluna: 'probabilidade',
        rotulo: 'Probabilidade',
        tipo: 'percentual',
        obrigatorio: true,
        // DS-542: a legenda que o fundador pediu. O exemplo não cita estágio: o da coleta
        // ("25% em Proposta") contradizia a semente, que dá 55% à Proposta.
        microcopy:
          'Chance de fechar um negócio que está neste estágio, de 0 a 100%. Entra no pipeline ponderado (funil, Previsão e Painel): cada negócio aberto conta valor × probabilidade. Ex.: com 25%, um negócio de R$ 100 mil conta R$ 25 mil.',
      },
      {
        coluna: 'outcome',
        rotulo: 'Resultado',
        tipo: 'escolha',
        obrigatorio: true,
        opcoes: ['aberto', 'ganho', 'perdido'],
        // DS-550: o banco grava o código; a tela mostra o rótulo, com a frase que a DS-542 deixou para este item.
        rotulosDasOpcoes: { aberto: 'Aberto', ganho: 'Ganho', perdido: 'Perdido' },
        microcopy: 'Aberto: o negócio segue no funil. Ganho e Perdido: estágios finais.',
      },
    ],
  },
  {
    slug: 'pracas',
    tabela: 'praca',
    rotulo: 'Praças',
    descricao:
      'A região da venda. É campo da Empresa, do Negócio e do Pedido, filtra as listas e é eixo das Metas.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativa',
    campos: [nome()],
  },
  {
    // DS-554 (item 09): "Cargos de contatos" sobrepõe o rótulo "Cargos" do protótipo 10.1 (html:108 e :242),
    // sem editar o protótipo; o slug continua `cargos`.
    slug: 'cargos',
    tabela: 'cargo',
    rotulo: 'Cargos de contatos',
    descricao:
      'O cargo de uma pessoa de contato na empresa em que ela atua (ex.: Head de Mídia), escolhido em cada vínculo do contato; aparece junto do contato em Contatos, nas fichas de Empresa e de Negócio, na Visão Geral, na cobrança e na busca. O posto de quem trabalha na DigitalView é a Função, em Produtividade & Pessoas.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'origens-negocio',
    tabela: 'origem_negocio',
    rotulo: 'Origens de negócio',
    descricao:
      'De onde veio o negócio (ex.: indicação, evento). Preenchida no formulário do Negócio; as Metas mostram as vendas por origem.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'motivos',
    tabela: 'motivo',
    rotulo: 'Motivos',
    descricao:
      'Os motivos pedidos ao perder um negócio, encerrar ou cancelar um pedido, cancelar uma NF, uma conta a pagar ou uma campanha, ignorar um movimento do extrato e rejeitar um pagamento. Cada motivo aparece só no seu Contexto.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    temChaveDeSistema: true,
    colunaAtivo: 'ativo',
    campos: [
      {
        coluna: 'contexto',
        rotulo: 'Contexto',
        tipo: 'escolha',
        obrigatorio: true,
        opcoes: CONTEXTOS_DE_MOTIVO.map((c) => c.valor),
        // DS-550: os rótulos de CONTEXTOS_DE_MOTIVO, que existiam e nunca tinham sido ligados.
        rotulosDasOpcoes: Object.fromEntries(CONTEXTOS_DE_MOTIVO.map((c) => [c.valor, c.rotulo])),
        microcopy: 'A tela em que o motivo é oferecido.',
      },
      nome(),
    ],
  },

  // ------------------------------------------------------- Financeiro & Fiscal
  {
    slug: 'perfis-prazo',
    tabela: 'perfil_prazo',
    rotulo: 'Perfis de prazo',
    descricao:
      'O prazo de recebimento do cliente, em dias. Cada Tipo de empresa tem um padrão, a Empresa pode ter o seu, e o pedido guarda os dias quando nasce: mudar aqui não altera os pedidos que já existem. As premissas de receita do Orçamento também o usam.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'dias',
        rotulo: 'Dias',
        tipo: 'inteiro',
        obrigatorio: true,
        microcopy: 'O Faturar em de cada parcela nasce este número de dias antes do vencimento.',
      },
    ],
  },
  {
    slug: 'aliquotas',
    tabela: 'aliquota',
    rotulo: 'Alíquotas de impostos',
    descricao:
      'Os tributos sobre a nota fiscal da entidade do topo, com vigência e fonte. Os vigentes no mês da NF formam as Deduções da Receita da DRE, descontam a margem das campanhas e calculam as deduções Calculadas do orçamento. Vigência que já cobre mês com NF autorizada não encolhe nem se exclui.',
    chave: 'id',
    rotuloDaLinha: 'tributo',
    porEntidade: true,
    faixaDeVigencia: { coluna: 'vigencia', de: 'vigencia_de', ate: 'vigencia_ate' },
    /*
      D-99: tributo, base e percentual são IMÓVEIS depois de salvos.

      Os três dizem o que a linha afirma sobre um período. Trocar o percentual de 2,5%
      para 3% na linha existente reinterpretaria toda nota já emitida sob 2,5% — e o
      erro seria invisível, porque a linha continua a mesma e a vigência dela continua
      cobrindo aquelas datas.

      Mudar uma alíquota é FECHAR a vigência desta e criar outra com o valor novo. Por
      isso a vigência continua editável: fechá-la é a operação, não o problema. O banco
      cobra o mesmo por gatilho (0030) — o formulário não é o único caminho.
    */
    campos: [
      {
        coluna: 'tributo',
        rotulo: 'Tributo',
        tipo: 'escolha',
        obrigatorio: true,
        imutavel: true,
        // DS-90, DS-558: só os tributos da nota. IRPJ e CSLL não entram em cálculo nenhum, e a
        // leitura de produção de 25/09 achou zero alíquota deles; o CHECK do banco fica para um
        // contract. CBS e IBS entram na v1.4.0 (D-167, DS-602): o catálogo os aceita, e nenhum
        // percentual vem semeado. Sem linha vigente, a dedução deles na DRE é zero.
        opcoes: ['ISS', 'PIS', 'COFINS', 'CBS', 'IBS'],
      },
      {
        coluna: 'percentual',
        rotulo: 'Percentual',
        tipo: 'percentual',
        obrigatorio: true,
        imutavel: true,
        microcopy: 'Para mudar a alíquota, encerre a vigência desta e cadastre outra.',
      },
      {
        coluna: 'base',
        rotulo: 'Base',
        tipo: 'escolha',
        obrigatorio: true,
        imutavel: true,
        // DS-625: a base é a receita da NF, a única que o sistema calcula; uma base escrita diferente escaparia da
        // guarda de sobreposição e somaria os percentuais (BACKLOG 4.49 b). O esquema fica aberto até o contract.
        opcoes: ['NF'],
        microcopy: 'O percentual incide sobre a receita da nota.',
      },
      {
        coluna: 'fonte',
        rotulo: 'Fonte',
        tipo: 'texto',
        obrigatorio: true,
        // D-179: de onde vem o percentual, dito como é. O orçamento só calcula com regra que tem fonte.
        microcopy: 'De onde vem o percentual: o contador, a lei ou o responsável do produto, com a data. Diga como é: confirmação que não houve não se escreve aqui.',
      },
      {
        coluna: 'vigencia_de',
        rotulo: 'Início da vigência',
        tipo: 'data',
        obrigatorio: true,
        microcopy: 'O primeiro dia do mês em que passa a valer.',
      },
      {
        coluna: 'vigencia_ate',
        rotulo: 'Fim da vigência',
        tipo: 'data',
        obrigatorio: false,
        microcopy: 'O primeiro dia do mês em que deixa de valer. Em branco = vale por tempo indeterminado.',
      },
    ],
  },
  {
    slug: 'tipos-pagamento',
    tabela: 'tipo_pagamento',
    rotulo: 'Tipos de pagamento',
    descricao:
      'Os meios de pagar uma despesa em Contas a Pagar. O tipo escolhido decide o primeiro passo dela: com NF ou RPA exigido, espera o documento; sem exigência, vai direto para aprovação.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    temChaveDeSistema: true,
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      // DS-555: o que as duas marcações fazem. O rótulo fica, porque é o chip da lista.
      {
        coluna: 'exige_nf',
        rotulo: 'Exige NF',
        tipo: 'booleano',
        obrigatorio: false,
        microcopy:
          'A despesa nasce em NF pendente e só vai para aprovação depois que a nota fiscal é registrada. Sem nenhuma exigência, ela vai direto para aprovação.',
      },
      {
        coluna: 'exige_rpa',
        rotulo: 'Exige RPA',
        tipo: 'booleano',
        obrigatorio: false,
        microcopy:
          'Mesmo passo da NF, com o RPA (Recibo de Pagamento a Autônomo), o recibo de quem presta serviço como pessoa física: a despesa nasce em RPA pendente e só vai para aprovação depois que ele é registrado.',
      },
    ],
  },
  {
    slug: 'centros-custo',
    tabela: 'centro_custo',
    rotulo: 'Centros de custo',
    descricao:
      'A área da DV que responde por um gasto. Contas a pagar, contratos do RH e regras de comissão levam um centro de custo, e o Orçamento e o Orçado × Realizado se dividem por ele.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'itens-contabeis',
    tabela: 'item_contabil',
    rotulo: 'Itens Contábeis',
    descricao:
      'Etiqueta opcional que filtra a DRE. A receita recebe sozinha o item de mesmo nome do produto quando o pedido tem um produto só (no pedido misto, cada parte com um produto só); nas contas a pagar e no Orçamento, o item se escolhe à mão.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [nome()],
  },
  {
    slug: 'moedas',
    tabela: 'moeda',
    rotulo: 'Moedas',
    descricao:
      'As moedas de pedidos, contas a pagar, contas bancárias e contratos do RH. A DRE e o caixa somam em reais, então toda outra moeda precisa de cotação.',
    chave: 'iso',
    rotuloDaLinha: 'nome',
    campos: [
      {
        coluna: 'iso',
        rotulo: 'ISO',
        tipo: 'texto',
        obrigatorio: true,
        imutavel: true,
        microcopy: 'Código de 3 letras (BRL, USD, EUR). Não muda depois de salvo.',
      },
      nome(),
      {
        coluna: 'casas_decimais',
        rotulo: 'Casas decimais',
        tipo: 'inteiro',
        obrigatorio: true,
        microcopy: 'Nem toda moeda tem centavos.',
      },
    ],
  },
  {
    slug: 'cotacoes',
    tabela: 'cotacao',
    rotulo: 'Cotações',
    descricao:
      'Quanto vale cada moeda em reais, por período. O valor em outra moeda é convertido pela cotação vigente na data dele; sem ela, a tela que soma avisa que falta a cotação.',
    chave: 'id',
    rotuloDaLinha: 'moeda_de',
    faixaDeVigencia: { coluna: 'vigencia', de: 'vigencia_de', ate: 'vigencia_ate' },
    campos: [
      { coluna: 'moeda_de', rotulo: 'Moeda de', tipo: 'referencia', obrigatorio: true, referencia: 'moedas' },
      { coluna: 'moeda_para', rotulo: 'Moeda para', tipo: 'referencia', obrigatorio: true, referencia: 'moedas' },
      { coluna: 'vigencia_de', rotulo: 'Início da vigência', tipo: 'data', obrigatorio: true },
      { coluna: 'vigencia_ate', rotulo: 'Fim da vigência', tipo: 'data', obrigatorio: false },
      {
        coluna: 'taxa',
        rotulo: 'Taxa',
        tipo: 'texto',
        obrigatorio: true,
        microcopy: 'Quantas unidades da Moeda para valem 1 unidade da Moeda de.',
      },
      {
        coluna: 'fonte',
        rotulo: 'Fonte',
        tipo: 'texto',
        obrigatorio: false,
        microcopy: 'De onde veio a taxa (ex.: Banco Central). Só para consulta.',
      },
    ],
  },
  {
    slug: 'paises',
    tabela: 'pais',
    rotulo: 'Países',
    descricao:
      'O país de empresas, contatos, fornecedores e colaboradores. Fora do Brasil, o Tipo de documento dá nome ao campo de documento da empresa, e a Máscara vira o exemplo mostrado no documento da empresa e no documento pessoal do contato.',
    chave: 'iso',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      { coluna: 'iso', rotulo: 'Código ISO', tipo: 'texto', obrigatorio: true, imutavel: true },
      nome(),
      {
        coluna: 'tipo_documento',
        rotulo: 'Tipo de documento',
        tipo: 'texto',
        obrigatorio: false,
        microcopy: 'Nome do documento fiscal da empresa deste país (ex.: RFC, EIN). No Brasil o campo se chama CNPJ.',
      },
      // DS-558: a Regra do documento não tem leitor; a coluna e o valor ficam no banco, fora da tela.
      {
        coluna: 'mascara_documento',
        rotulo: 'Máscara do documento',
        tipo: 'texto',
        obrigatorio: false,
        microcopy: 'O exemplo mostrado no campo de documento de empresas e contatos deste país.',
      },
    ],
  },
  {
    slug: 'regimes-fiscais',
    tabela: 'regime_fiscal',
    rotulo: 'Regimes fiscais',
    descricao:
      'O regime tributário de cada Entidade DV. Ele não entra em cálculo nenhum: os tributos da DRE e do orçamento vêm de Alíquotas de impostos. A entidade troca de regime pelo botão Registrar regime, com fonte e motivo, e o fechamento de IRPJ e CSLL exige um regime registrado.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    // DS-558: os Parâmetros do regime não têm leitor; a coluna e o valor ficam no banco, fora da tela.
    campos: [nome()],
  },
  {
    slug: 'provedores-nfse',
    tabela: 'provedor_nfse',
    rotulo: 'Provedores de NFS-e',
    descricao:
      'O sistema de NFS-e da prefeitura que a emissão automática vai usar, escolhido nos Dados fiscais de emissão de cada Entidade DV.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    // D-147: o cadastro fica para a emissão automática, com o aviso de que ela ainda não existe.
    aviso: AVISO_DA_EMISSAO_AUTOMATICA,
    campos: [
      nome(),
      { coluna: 'tipo_integracao', rotulo: 'Tipo de integração', tipo: 'texto', obrigatorio: true },
      { coluna: 'config', rotulo: 'Configuração', tipo: 'json', obrigatorio: false },
    ],
  },

  // -------------------------------------------------- Produtividade & Pessoas
  {
    slug: 'estagios-tarefa',
    tabela: 'estagio_tarefa',
    rotulo: 'Estágios de tarefas',
    descricao:
      'As colunas do quadro de Tarefas, na ordem desta lista. A tarefa que entra no estágio marcado como Conclui a tarefa fica concluída.',
    efeitoDeDesativar:
      'Estágio inativo sai do quadro e das opções; as tarefas que estão nele deixam de aparecer no quadro, mas seguem na ficha e na busca.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    colunaOrdem: 'ordem',
    campos: [
      nome(),
      {
        coluna: 'conclui',
        rotulo: 'Conclui a tarefa',
        tipo: 'booleano',
        obrigatorio: false,
        microcopy: 'Ao entrar neste estágio a tarefa é marcada como concluída.',
      },
    ],
  },
  {
    slug: 'departamentos',
    tabela: 'departamento',
    rotulo: 'Departamentos',
    descricao:
      'Onde cada colaborador trabalha. Filtra o quadro do RH e o Headcount por departamento, e o Centro de custo espelhado é o que o cadastro do colaborador sugere para o contrato.',
    efeitoDeDesativar:
      'Departamento inativo sai das opções. O cadastro de quem ainda está nele deixa de salvar, inclusive o desligamento pela ficha, até a pessoa passar para um departamento ativo ou ele ser reativado.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    campos: [
      nome(),
      {
        coluna: 'centro_custo_id',
        rotulo: 'Centro de custo espelhado',
        tipo: 'referencia',
        obrigatorio: true,
        referencia: 'centros-custo',
        microcopy: 'O centro que o cadastro do colaborador sugere para o contrato de quem está neste departamento.',
      },
    ],
  },
  {
    slug: 'funcoes',
    tabela: 'funcao',
    rotulo: 'Funções',
    descricao:
      'O cargo de quem trabalha na DV, com as responsabilidades dele. Aparece no quadro e na ficha do colaborador.',
    efeitoDeDesativar:
      'Função inativa sai das opções. O cadastro de quem ainda a tem deixa de salvar, inclusive o desligamento pela ficha, até a pessoa receber uma função ativa ou ela ser reativada.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativa',
    campos: [
      nome(),
      {
        coluna: 'descricao_responsabilidades',
        rotulo: 'Descrição e responsabilidades',
        tipo: 'texto-longo',
        obrigatorio: true,
      },
    ],
  },
  {
    slug: 'tipos-contratacao',
    tabela: 'tipo_contratacao',
    rotulo: 'Tipos de contratação',
    descricao:
      'A modalidade do contrato de um colaborador. Ao nascer, o contrato copia do tipo o documento exigido no pagamento e a retenção na fonte.',
    chave: 'id',
    rotuloDaLinha: 'nome',
    colunaAtivo: 'ativo',
    temSemente: true,
    campos: [
      nome(),
      {
        coluna: 'explicacao_regime',
        rotulo: 'Explicação do regime',
        tipo: 'texto-longo',
        obrigatorio: true,
        microcopy: 'Aparece sob o Tipo de contratação no cadastro do contrato do RH.',
      },
      /*
        PÓS-V1-021 — a exigência de PJ é CONFIGURÁVEL, e é esta linha que a torna.

        Sem ela, uma modalidade nova criada aqui nasceria com o `DEFAULT false` e ninguém
        conseguiria mudá-lo sem migration — que é o estado das outras três políticas do
        catálogo (`documento_padrao`, `deducao_padrao_pct`, `tributo_retido_padrao`),
        limitação preexistente que este pacote não corrige.
      */
      {
        coluna: 'exige_pessoa_juridica',
        rotulo: 'Exige pessoa jurídica',
        tipo: 'booleano',
        obrigatorio: false,
        microcopy: 'O contrato deste tipo é pago a uma empresa contratada (CNPJ), e não à pessoa.',
      },
    ],
  },

  // ------------------------------------------------------------------ Sistema
  {
    slug: 'parametros',
    tabela: 'parametro_sistema',
    rotulo: 'Parâmetros do sistema',
    descricao:
      'Números que o sistema usa em cálculos e avisos: margem das campanhas, alerta da Rentabilidade, vencimento da guia do RH e capacidade da agenda de Tarefas. A chave é fixa; só o valor se edita.',
    chave: 'id',
    rotuloDaLinha: 'chave',
    somenteEdicao: true,
    campos: [{ coluna: 'valor', rotulo: 'Valor', tipo: 'texto', obrigatorio: true }],
    camposDeLeitura: [
      { coluna: 'chave', rotulo: 'Chave', tipo: 'texto', obrigatorio: false },
      { coluna: 'descricao', rotulo: 'Descrição', tipo: 'texto', obrigatorio: false },
      { coluna: 'tipo', rotulo: 'Tipo', tipo: 'texto', obrigatorio: false },
    ],
  },
]

/**
 * DS-543: regra de VALOR por chave de parâmetro.
 *
 * O descritor `parametros` tem um campo só (`valor`, texto) cujo sentido muda com a chave;
 * por isso a regra é por chave. A 10.1 confere antes de enviar, o PATCH confere antes do
 * SQL, e o CHECK `parametro_sistema_dia_da_guia_rh` (0147) repete a faixa no banco.
 * O tipo por chave também orienta a normalização da tela: uma linha com tipo divergente
 * no banco não pode fazer as duas fronteiras aceitarem formatos diferentes.
 */
const DIA_DA_GUIA = 'Use um dia de 1 a 28: 29, 30 e 31 não existem em todo mês.'
const LIGADO_OU_DESLIGADO = 'Escolha Sim ou Não.'
const PERCENTUAL = 'Informe uma fração de 0 a 1, usando ponto decimal.'
const MOEDA = 'Informe um valor monetário com ponto e até duas casas decimais.'
const NUMERO = 'Informe um número com ponto decimal.'
const DIAS_INTEIROS = 'Informe um número inteiro de dias na faixa de -2147483648 a 2147483647.'

type TipoDoParametro = 'numero' | 'percentual' | 'moeda' | 'booleano'

/** As chaves editáveis: as onze do seed produtivo e a do DV360 (0199); chave desconhecida mantém a política anterior. */
export const TIPO_POR_CHAVE_DE_PARAMETRO: Readonly<Record<string, TipoDoParametro>> = {
  'rentabilidade.faixa_alerta_reconciliacao_pct': 'percentual',
  'margem.licenciamento_pct': 'percentual',
  'margem.ir_csll_estimado_pct': 'percentual',
  'margem.meta_minima_pct': 'percentual',
  'faturamento.prazo_emissao_nf_dias': 'numero',
  'tarefas.capacidade_dia_util_h': 'numero',
  'tarefas.capacidade_fim_semana_h': 'numero',
  'rh.guia_retencao_dia_vencimento': 'numero',
  'sicoob.leitura_diaria_ativa': 'booleano',
  'sicoob.leitura_intradiaria_ativa': 'booleano',
  // P-43: a sincronização diária do DV360 nasce desligada (0199); liga-se na ativação da integração.
  'dv360.sincronizacao_diaria_ativa': 'booleano',
  'dv360.intradiario_ativo': 'booleano',
  'dv360.intradiario_intervalo_horas': 'numero',
  'dv360.intradiario_inicio_hora': 'numero',
  'dv360.intradiario_fim_hora': 'numero',
}

export function tipoDoValorDoParametro(chave: string): TipoDoParametro | undefined {
  return Object.hasOwn(TIPO_POR_CHAVE_DE_PARAMETRO, chave)
    ? TIPO_POR_CHAVE_DE_PARAMETRO[chave]
    : undefined
}

const VALOR_POR_TIPO: Readonly<Record<TipoDoParametro, z.ZodType<string>>> = {
  percentual: z.string({ error: PERCENTUAL }).regex(FRACAO_ENTRE_ZERO_E_UM, PERCENTUAL),
  moeda: z.string({ error: MOEDA }).regex(/^-?\d+(?:\.\d{1,2})?$/, MOEDA),
  numero: z.string({ error: NUMERO }).regex(/^-?\d+(?:\.\d+)?$/, NUMERO),
  booleano: z.enum(['true', 'false'], { error: LIGADO_OU_DESLIGADO }),
}

const VALOR_POR_PARAMETRO: Readonly<Record<string, z.ZodType<string>>> = {
  'rh.guia_retencao_dia_vencimento': z.string({ error: DIA_DA_GUIA }).regex(/^(?:[1-9]|1\d|2[0-8])$/, DIA_DA_GUIA),
  // A view legada faz valor::integer (int4). Só o formato é validado; seu papel não muda.
  'faturamento.prazo_emissao_nf_dias': z.string({ error: DIAS_INTEIROS })
    .regex(/^-?\d+$/, DIAS_INTEIROS)
    .refine((valor) => Number(valor) >= -2147483648 && Number(valor) <= 2147483647, DIAS_INTEIROS),
  // D-160: os controles da leitura automática do Sicoob.
  'sicoob.leitura_diaria_ativa': z.enum(['true', 'false'], { error: LIGADO_OU_DESLIGADO }),
  'sicoob.leitura_intradiaria_ativa': z.enum(['true', 'false'], { error: LIGADO_OU_DESLIGADO }),
  'dv360.sincronizacao_diaria_ativa': z.enum(['true', 'false'], { error: LIGADO_OU_DESLIGADO }),
  'dv360.intradiario_ativo': z.enum(['true', 'false'], { error: LIGADO_OU_DESLIGADO }),
  'dv360.intradiario_intervalo_horas': z.string().regex(/^(?:[1-9]|1[0-9]|2[0-4])$/, 'Informe um intervalo inteiro entre 1 e 24 horas.'),
  'dv360.intradiario_inicio_hora': z.string().regex(/^(?:[0-9]|1[0-9]|2[0-3])$/, 'Informe uma hora inteira entre 0 e 23.'),
  'dv360.intradiario_fim_hora': z.string().regex(/^(?:[1-9]|1[0-9]|2[0-4])$/, 'Informe uma hora inteira entre 1 e 24, posterior ao início.'),
}

/** A frase da recusa, ou null quando o valor serve (ou a chave é desconhecida). */
export function recusaDoValorDoParametro(chave: string, valor: unknown): string | null {
  const tipo = tipoDoValorDoParametro(chave)
  if (tipo === undefined) return null
  const regra = VALOR_POR_PARAMETRO[chave] ?? VALOR_POR_TIPO[tipo]
  const lido = regra.safeParse(typeof valor === 'number' ? String(valor) : valor)
  return lido.success ? null : (lido.error.issues[0]?.message ?? 'Valor inválido.')
}

const POR_SLUG = new Map(DESCRITORES.map((d) => [d.slug, d]))

export function descritorDe(slug: string): DescritorDeCatalogo | undefined {
  return POR_SLUG.get(slug)
}

export const TIPOS_DE_CATALOGO: readonly string[] = DESCRITORES.map((d) => d.slug)

/**
 * Uma linha de catálogo, como a API a devolve.
 *
 * `campos` é um mapa aberto porque cada catálogo tem os seus — mas as CHAVES desse
 * mapa não são livres: são as `coluna` do descritor, e o servidor só devolve e só
 * aceita essas.
 */
export const valorDeCampo = z.union([z.string(), z.number(), z.boolean(), z.null()])

export const itemDeCatalogo = z.object({
  id: z.string(),
  rotulo: z.string(),
  ativo: z.boolean().nullable(),
  ordem: z.number().int().nullable(),
  semente: z.boolean(),
  /**
   * DS-598: a identidade técnica da linha que o sistema procura (`chave_sistema`), só nos catálogos
   * com `temChaveDeSistema`; null nas linhas comuns. A tela reconhece a linha por ela, e não pelo
   * rótulo, que o usuário pode trocar.
   */
  chaveSistema: z.string().nullable().optional(),
  uso: z.number().int().optional(),
  campos: z.record(z.string(), valorDeCampo),
})
export type ItemDeCatalogo = z.infer<typeof itemDeCatalogo>

export const entradaDeItemDeCatalogo = z.object({ campos: z.record(z.string(), valorDeCampo) })
export type EntradaDeItemDeCatalogo = z.infer<typeof entradaDeItemDeCatalogo>

/** Resposta do 409 de exclusão (DS-27): a contagem é o que a tela mostra. */
export const conflitoDeUso = z.object({ uso: z.number().int() })

/** Rename responde isto, e a tela avisa que leituras históricas mudam de nome. */
export const respostaDeEdicao = z.object({
  item: itemDeCatalogo,
  avisoHistorico: z.boolean().optional(),
})
export type RespostaDeEdicao = z.infer<typeof respostaDeEdicao>
